<script setup>
import { MediaCard } from '@dashhudson/dashing-ui/media-card';
import Formatters from '@dashhudson/dashing-ui/utils/formatters';
import { camelize, camelizeKeys } from 'humps';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { computed } from 'vue';

import { useMediaData } from '@/app/library/composables/useMediaData';
import { DEFAULT_METRIC_LABELS, DEFAULT_METRICS, MEDIA_CARD_FORMAT } from '@/config';
import { META_ADS_CHANNELS } from '@/models/dashboards/channels.enum';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { usePlatformStore } from '@/stores/platform';
import { localize } from '@/utils/formatters';

const platformStore = usePlatformStore();

const scaledPercentageMetrics = [
  'averageCompletionRate',
  'avgViewPercentage',
  'cardClickRate',
  'cardTeaserClickRate',
  'clickThroughRate',
  'completionRate',
  `completionRateViews`,
  'effectiveness',
  'effectivenessEngagements',
  'engagement',
  'engagementRate',
  'engagementRateImpressions',
  'engagementRatePublic',
  'exitRate',
  'exitRateViews',
  'fullVideoWatchedRate',
  'impressionsForYouRate',
  'reel.effectiveness',
  'ugcEffectiveness',
  'organicEffectiveness',
  'organicEngagementRate',
  'paidEngagementRate',
  'paidTotalEngagementRate',
  'paidAndOrganicEngagementRate',
  'paidAndOrganicEffectivenessV2',
  'paidAndOrganicEngagementRateV2',
  'impressionsPersonalProfileRate',
  'impressionsSearchRate',
  'impressionsSoundRate',
  'impressionsFollowRate',
  'impressionsHashtagRate',
  'engagementRateViews',
  'organicEffectivenessV2',
  'paidEffectivenessV2',
  'paidAndOrganicEffectivenessV2',
  'organicEngagementRateV2',
  'paidEngagementRateV2',
  'paidAndOrganicEngagementRateV2',
  'viewsBasedEngagementRate',
  'reelsSkipRate',
];
const directPercentageMetrics = ['ctr'];
const secondTimeMetrics = [
  'averageTimeWatched',
  'avgViewDuration',
  'estSecondsWatched',
  'estPremiumSecondsWatched',
  'totalTimeWatched',
  'totalTimeViewedSec',
  'totalTimeViewedSecLong',
  'duration',
  'avgViewTimeSeconds',
  'viewTimeSeconds',
  'avgTimeViewedSec',
];
const millisecondTimeMetrics = [
  'averageVideoWatchTime',
  'videoAnalyticsTimeWatchedForViews',
  'videoAnalytics.timeWatchedForViews',
  'reel.post_video_view_time',
  'reel.postVideoAvgTimeWatched',
  'reel.postVideoViewTime',
];
const twoDecimalMetrics = ['entertainmentScore', 'frequency'];
const monetaryMetrics = [
  'cpc',
  'cpm',
  'amountSpend',
  'amountSpent',
  'costPerThruplays',
  'costPerThruplay',
];

const props = defineProps({
  media: {
    type: Object,
    required: true,
  },
  showMetrics: {
    type: Boolean,
    default: true,
  },
  displayMetrics: {
    type: Array,
    default: null,
  },
  metricLabels: {
    type: Object,
    default: null,
  },
  metricTypes: {
    type: Object,
    default: null,
  },
  metricTooltips: {
    type: Object,
    default: null,
  },
  highlightedMetricFieldName: {
    type: String,
    default: '',
  },
  menuItems: {
    type: Array,
    default: null,
  },
  avatar: {
    type: [String, Object],
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  caption: {
    type: String,
    default: null,
  },
  showUserDetails: {
    type: Boolean,
    default: false,
  },
  includeCaption: {
    type: Boolean,
    default: false,
  },
  // When false, the card is non-interactive - no inner click binding (no media drawer,
  // no wrapper `click` emit, and dashing-ui skips interactive hover treatment).
  interactive: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['click']);

const {
  channelKey,
  channelName,
  sourceData,
  mediaSrc,
  mediaType,
  platformMediaType,
  mediaText,
  mediaDate,
  mediaDuration,
  mediaCaption,
  mediaAvatar,
  mediaHandle,
  mediaSentiment,
  visionPrediction,
  isPromoted,
  isUGC,
  openMediaDrawer,
} = useMediaData(props.media);

const highlightedMetricField = computed(() => camelize(props.highlightedMetricFieldName));

const brandId = computed(() => camelizeKeys(props.media)?.brandId);

// Instagram connections are stored under 'facebook' in platformConnectionsMap
const platformConnectionKey = computed(() => {
  if (channelKey.value === 'instagram' || channelKey.value === 'instagram_story_frame') {
    return 'facebook';
  }
  return channelKey.value;
});

const resolvedTitle = computed(() => {
  if (!props.showUserDetails) return props.title;
  return (
    props.title ??
    mediaHandle.value ??
    platformStore.platformConnectionsMap?.[platformConnectionKey.value]?.[brandId.value]?.handle ??
    platformStore.platformConnectionsMap?.[platformConnectionKey.value]?.[brandId.value]
      ?.email_or_handle ??
    null
  );
});

const resolvedAvatar = computed(() => {
  if (!props.showUserDetails) return props.avatar;

  const imageUrl =
    props.avatar?.image ??
    (typeof props.avatar === 'string' ? props.avatar : null) ??
    mediaAvatar.value ??
    platformStore.platformConnectionsMap?.[platformConnectionKey.value]?.[brandId.value]
      ?.avatar_url ??
    null;

  if (imageUrl) {
    return { image: imageUrl };
  }

  // Use initials from handle as placeholder if avatar is missing
  const handle = resolvedTitle.value;
  if (handle) {
    return { label: handle };
  }

  return null;
});

const resolvedCaption = computed(() => {
  if (!props.showUserDetails && !props.includeCaption) return props.caption;
  return props.caption ?? mediaCaption.value ?? null;
});

const defaultDisplayMetrics = computed(() => DEFAULT_METRICS[channelKey.value] || []);

const displayMetricLabels = computed(
  () => props.metricLabels || DEFAULT_METRIC_LABELS[channelKey.value] || {},
);

const displayMetrics = computed(() => {
  if (!props.showMetrics) return [];

  const metrics = cloneDeep(
    props.displayMetrics?.length ? props.displayMetrics : defaultDisplayMetrics.value,
  ).map((field) => camelize(field));

  if (Boolean(highlightedMetricField.value) && !metrics.includes(highlightedMetricField.value)) {
    metrics.push(highlightedMetricField.value);
  }

  return metrics.map((field) => camelize(field));
});

function formatCurrency(value) {
  const formattedValue = Formatters.formatCurrency(value);

  if (META_ADS_CHANNELS.includes(channelKey.value?.toUpperCase())) {
    const metaAdsAccounts = platformStore.getAccount(PLATFORM_CONNECTION.FACEBOOK_ADS);
    const currency = metaAdsAccounts?.[0]?.currency;
    return Formatters.formatCurrency(value, {
      includeCurrency: true,
      formatOptions: { currency },
    });
  }

  return formattedValue;
}

function formatMetricItem(metricName) {
  // We use `get` here since a `metricName` can be a path to the value e.g. reel.fbReelsTotalPlays
  let metricValue = get(sourceData.value, metricName);
  // Case for Instagram, where no access to private effectiveness: `effectivenessEngagements`
  // Default to public effectiveness: `effectiveness`
  // But do NOT default if the Media is Owned, only unowned media should have this behaviour
  // TODO: Sorting will still not work, need a solution for cross-metric sorting between private/public effectiveness
  if (
    !metricValue &&
    metricName === 'effectivenessEngagements' &&
    get(sourceData.value, 'sourceType') !== 'OWNED'
  ) {
    metricValue = get(sourceData.value, 'effectiveness');
  }

  // Edge case where some metrics are -1 when they should be undefined.
  if (metricValue === -1) metricValue = undefined;

  let format = MEDIA_CARD_FORMAT.NUMBER;

  // If the metric is null/not in the source data, we don't apply any formatting
  if (metricValue == null) {
    format = null;
  } else if (scaledPercentageMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.PERCENTAGE;
  } else if (directPercentageMetrics.includes(metricName)) {
    format = null;
    metricValue = metricValue.toFixed(2);
  } else if (twoDecimalMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.TWO_DECIMAL;
  } else if (secondTimeMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.DURATION;
  } else if (millisecondTimeMetrics.includes(metricName)) {
    format = MEDIA_CARD_FORMAT.DURATION_MILLISECONDS;
  } else if (monetaryMetrics.includes(metricName)) {
    format = null;
    metricValue = formatCurrency(metricValue);
  } else if (metricName === 'emv') {
    format = null;
    metricValue = `$${localize(metricValue)}`;
  }

  return {
    value: metricValue,
    label: displayMetricLabels.value[metricName] || startCase(metricName),
    postType: props.metricTypes?.[metricName],
    tooltip: props.metricTooltips?.[metricName],
    highlight: highlightedMetricField.value === metricName,
    ...format,
  };
}

const metricItems = computed(() => {
  if (!displayMetrics.value.length) return null;

  return displayMetrics.value.map((metricName) => formatMetricItem(metricName));
});

function onClickMedia() {
  emit('click');
  openMediaDrawer();
}

const mediaCardListeners = computed(() => (props.interactive ? { click: onClickMedia } : {}));
</script>

<template>
  <MediaCard
    :class="{ '!cursor-pointer': props.interactive }"
    :src="mediaSrc"
    :source-type="mediaType"
    :platform-media-type="platformMediaType"
    :video-duration="mediaDuration"
    video-preload="none"
    :video-poster="mediaSrc"
    :ugc="isUGC"
    :text="mediaText"
    :channel="channelName"
    :post-date="mediaDate"
    :promoted="isPromoted"
    :vision-prediction="visionPrediction"
    :metric-items="metricItems"
    :menu-items="props.menuItems"
    :avatar="resolvedAvatar"
    :title="resolvedTitle"
    :caption="resolvedCaption"
    :sentiment="props.showMetrics ? mediaSentiment : ''"
    :sentiment-readonly="true"
    v-on="mediaCardListeners"
  />
</template>
